/**
 * Phase 5 Task 8 — dialer Zustand store.
 *
 * Holds campaign list, the active campaign, the current preview contact,
 * and real-time pacing metrics. Data is fed from the campaign/dialer
 * socket events; the store contract is stable and lets the UI render
 * immediately once the feed is wired.
 */
import { create } from 'zustand'

export interface Campaign {
  id: string
  name: string
  // ITEM 63 — the backend accepts seven UPPERCASE modes
  // (routes/campaigns/schemas.ts:14). This union offered three lowercase
  // values the API rejects, so MANUAL/POWER/ROBO/AMD campaigns could not be
  // represented and the three that could were mis-cased.
  mode: 'MANUAL' | 'PREVIEW' | 'PROGRESSIVE' | 'POWER' | 'PREDICTIVE' | 'ROBO' | 'AMD'
  status: 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'DRAFT'
  maxCps: number
  maxAttemptsPerDay: number
  callingHoursStart: string | null
  callingHoursEnd: string | null
  config: Record<string, unknown>
  pacingFactor?: number
  abandonRate?: number
}

export interface PreviewContact {
  id: string
  name: string
  phone: string
  notes?: string
}

interface Pacing {
  dialRate: number
  abandonRate: number
  pacingFactor: number
}

interface DialerStoreState {
  campaigns: Campaign[]
  activeCampaign: Campaign | null
  previewContact: PreviewContact | null
  pacing: Pacing
  setCampaigns(campaigns: Campaign[]): void
  setActiveCampaign(campaign: Campaign | null): void
  setPreviewContact(contact: PreviewContact | null): void
  setPacing(update: Partial<Pacing>): void
  reset(): void
}

const initialState = {
  campaigns: [] as Campaign[],
  activeCampaign: null,
  previewContact: null,
  pacing: { dialRate: 0, abandonRate: 0, pacingFactor: 1 },
} satisfies Pick<
  DialerStoreState,
  'campaigns' | 'activeCampaign' | 'previewContact' | 'pacing'
>

export const useDialerStore = create<DialerStoreState>()((set) => ({
  ...initialState,
  setCampaigns: (campaigns) => set({ campaigns }),
  setActiveCampaign: (activeCampaign) => set({ activeCampaign }),
  setPreviewContact: (previewContact) => set({ previewContact }),
  setPacing: (update) =>
    set((state) => ({ pacing: { ...state.pacing, ...update } })),
  reset: () => set({ ...initialState }),
}))
